import React from 'react';
import { Play, CheckCircle2 } from 'lucide-react';
import { Touchable } from '../touchable/Touchable';

export interface LifecycleMarker {
  kind: 'start' | 'finish';
  color: string;
}

interface DayCellProps {
  dayNumber: number;
  totalMs: number;
  dominantColor: string | null;
  markers: LifecycleMarker[];
  isToday: boolean;
  isSelected: boolean;
  onSelect: () => void;
}

function tintClass(ms: number): string {
  if (ms <= 0) return '';
  if (ms < 15 * 60_000) return 'bg-soft/30';
  if (ms < 45 * 60_000) return 'bg-soft/50';
  if (ms < 120 * 60_000) return 'bg-soft/70';
  return 'bg-soft';
}

function formatTime(ms: number): string {
  if (ms <= 0) return '';
  const totalMin = Math.round(ms / 60_000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h${m.toString().padStart(2, '0')}`;
}

export const DayCell: React.FC<DayCellProps> = ({
  dayNumber,
  totalMs,
  dominantColor,
  markers,
  isToday,
  isSelected,
  onSelect,
}) => {
  const tint = tintClass(totalMs);
  const timeLabel = formatTime(totalMs);
  const borderClass = isSelected
    ? 'border-2 border-primary'
    : 'border border-transparent';
  const ringClass = isToday ? 'ring-2 ring-primary ring-inset' : '';

  return (
    <Touchable
      haptic="softTap"
      onClick={onSelect}
      className={`relative aspect-square rounded-lg ${tint} ${borderClass} ${ringClass} text-left p-1 hover:bg-soft/40 transition-colors`}
    >
      <span className="text-xs text-ink font-medium">{dayNumber}</span>
      {timeLabel && (
        <span
          data-testid="day-time"
          className="block text-[10px] text-ink-faint tabular-nums"
        >
          {timeLabel}
        </span>
      )}
      {dominantColor && (
        <span
          data-testid="day-bar"
          aria-hidden
          style={{ backgroundColor: dominantColor }}
          className="absolute bottom-0 left-0 right-0 h-1 rounded-b-lg"
        />
      )}
      {markers.length > 0 && (
        <span className="absolute top-1 right-1 flex items-center gap-1">
          {markers.map((m, i) => {
            // Color disambiguates same-kind markers; index disambiguates
            // same-kind-same-color markers (rare but possible if two sessions
            // on the same pattern start on the same day).
            const key = `${m.kind}-${m.color}-${i}`;
            return m.kind === 'start' ? (
              <Play
                key={key}
                aria-label="started"
                size={12}
                style={{ color: m.color }}
                fill="currentColor"
              />
            ) : (
              <CheckCircle2
                key={key}
                aria-label="finished"
                size={12}
                style={{ color: m.color }}
              />
            );
          })}
        </span>
      )}
    </Touchable>
  );
};
